// RateAddEditModal.js
import React, { useState, useEffect, useMemo } from "react";
import CloseIcon from "@mui/icons-material/Close";
import LockIcon from '@mui/icons-material/Lock'; // ADDED: For visual feedback on fixed fields
import InfoIcon from '@mui/icons-material/Info'; // ADDED
import { Input } from "../../../../../common/Input";
import { useEnumsApi } from "../../../../hooks/useEnumsApi";

// hide for now but dont remove, since where utilizing the Operation Benefit
const showAllowance = false;

/**
 * RateAddEditModal - Professional "Locked Criteria" Version
 * This modal allows editing of rate values while keeping the matching criteria 
 * fixed based on the current filter state.
 */
const RateAddEditModal = ({ mode = "Add", isOpen, onClose, selectedItem, onSave }) => {
  const { maintenanceTypes, fetchMaintenanceData } = useEnumsApi();
  const [valueLists, setValueLists] = useState({});
  const [errors, setErrors] = useState({});

  const [form, setForm] = useState({
    category: "",
    salary: 0,
    allowance: 0,
    details: []
  });

  // MODIFIED: Enhanced data loading logic
  useEffect(() => {
    if (selectedItem) {
      setForm(selectedItem);
      setErrors({});

      // Load the readable text for all maintenance values
      if (selectedItem.details?.length > 0) {
        const typeIds = selectedItem.details.map(d => d.maintenanceTypeId);
        loadValuesForTypes(typeIds);
      }
    }
  }, [selectedItem, isOpen]);

  const loadValuesForTypes = async (typeIds) => {
    const missingIds = typeIds.filter(id => !valueLists[id]);
    if (missingIds.length === 0) return;

    const data = await fetchMaintenanceData(missingIds);
    if (data) {
      setValueLists(prev => ({ ...prev, ...data }));
    }
  };

  // Find the text label for a specific maintenance type and value
  const getLookupText = (typeId, valueId) => {
    // 1. Find the Category Name (e.g., "Employee Type")
    const type = maintenanceTypes.find(t =>
      t.value?.toString() === typeId?.toString()
    );

    // 2. Get the list for this type
    const list = valueLists[typeId?.toString()] || [];

    // 3. Find the specific value object
    // Checks both 'value' (your sample) and 'id' (legacy)
    const item = list.find(v => {
      const compareId = v.value !== undefined ? v.value : v.id;
      return compareId?.toString() === valueId?.toString();
    });

    return {
      typeName: type ? type.name : "Unknown",
      valueName: item
        ? (item.label || "N/A")
        : "Loading..."
    };
  };

  const validateForm = () => {
    const newErrors = {};
    if (!form.category?.trim()) newErrors.category = "Category is required";
    if (form.salary === "" || form.salary === null) newErrors.salary = "Salary is required";
    if (form.allowance === "" || form.allowance === null) newErrors.allowance = "Allowance is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInternalSave = () => {
    if (validateForm()) {
      onSave(form);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-2xl rounded-lg shadow-2xl flex flex-col max-h-[90vh]">

        {/* Header */}
        <div className="px-6 py-4 border-b flex justify-between items-center rounded-t-lg bg-gray-100/50">
          <div>
            <h2 className="text-xl font-bold">{mode === "Add" ? "Create" : "Update"} Rate Rule</h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-200 transition-colors border-none bg-transparent cursor-pointer">
            <CloseIcon className="text-gray-500" />
          </button>
        </div>

        <div className="p-8 overflow-y-auto space-y-8">

          {/* Section 1: Fixed Matching Criteria (Locked Zone) */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-blue-600">
              <LockIcon fontSize="small" />
              <h3 className="font-bold text-sm uppercase tracking-wider">Applied Matching Criteria</h3>
            </div>

            <div className="bg-blue-50/50 border border-blue-100 rounded-lg p-5 grid grid-cols-2 gap-y-4 gap-x-6">
              {form.details.length > 0 ? (
                form.details.map((detail, index) => {
                  const lookups = getLookupText(detail.maintenanceTypeId, detail.maintenanceValueId);
                  return (
                    <div key={index} className="flex flex-col border-l-2 border-blue-200 pl-3">
                      <span className="text-[10px] font-bold text-blue-500 uppercase">{lookups.typeName}</span>
                      <span className="text-sm font-semibold text-gray-700">{lookups.valueName}</span>
                    </div>
                  );
                })
              ) : (
                <div className="col-span-2 text-sm text-gray-400 italic">No criteria assigned.</div>
              )}
            </div>
          </div>

          {/* Section 2: Rate Configuration (Editable Zone) */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-gray-700">
              <h3 className="font-bold text-sm uppercase tracking-wider">Rate Details</h3>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <Input
                label="Category"
                placeholder="e.g. F4, Base Rate"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                error={!!errors.category}
                errorMessage={errors.category}
              />
              <Input
                label="Daily Salary Rate"
                placeholder="0.00"
                type="number"
                value={form.salary}
                onChange={(e) => setForm({ ...form, salary: e.target.value })}
                error={!!errors.salary}
                errorMessage={errors.salary}
                className="bg-white"
              />
              {showAllowance && (
                <Input
                  label="Daily Allowance"
                  placeholder="0.00"
                  type="number"
                  value={form.allowance}
                  onChange={(e) => setForm({ ...form, allowance: e.target.value })}
                  error={!!errors.allowance}
                  errorMessage={errors.allowance}
                  className="bg-white"
                />
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t flex justify-end gap-3 rounded-b-lg bg-gray-100/50">
          <button
            onClick={onClose}
            className="px-4 py-2 border rounded bg-white cursor-pointer hover:bg-gray-100"
          >
            CANCEL
          </button>
          <button
            onClick={handleInternalSave}
            className="px-6 py-2 bg-blue-600 text-white rounded cursor-pointer border-none hover:bg-blue-700"
          >
            SAVE RULE
          </button>
        </div>
      </div>
    </div>
  );
};

export default RateAddEditModal;